import * as React from 'react';
import * as translations from './strings';
import * as columnTranslations from './stringsColumnPlans';
import styles from './PlanCardColumn.module.css';
import { UserOutlined as User } from '@ant-design/icons';
import { getPlanFeatures, getPlanDescription } from './PlanFeatures';
import StandardIcon from './images/icon-pricing-plan-standard.svg';
import StudioIcon from './images/icon-pricing-plan-studio.svg';
import PremiumIcon from './images/icon-pricing-plan-premium.svg';
import DeluxeIcon from './images/icon-pricing-plan-deluxe.svg';
import PinIcon from './images/icon-most-popular-plan-pin.svg';
import { PlanInterval } from '../types';
import SubscribeButton from './SubscribeButton';
import FeatureColumn from './FeatureColumn';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { SubscriptionPlan, SubscriptionTrial } from '../query';
import { PlanFeatureItem } from './PlanFeatures';

const strings = translations['en'];
const columnStrings = columnTranslations['en'];

const getPopupContainer = () =>
  document.getElementById('settings_container') as HTMLElement;

interface Props {
  plan: {
    id: string;
    name: string;
    price: number;
    handle: string;
    interval: PlanInterval;
    maxMembers: number;
  };
  organization: {
    id: string;
    numMembers: number;
    validPartnerCoupon: boolean;
    activePartnerCouponDescription: string;
    partnerCouponName: string | null;
    experiments: string[];
  };
  subscription: SubscriptionPlan | SubscriptionTrial;
}

function pluralize(count: number, word: string) {
  return count === 1 ? `${count} ${word}` : `${count} ${word}s`;
}

const getPlanIcon = (handle: string): React.ReactNode => {
  switch (handle) {
    case 'basic':
    case 'basic_yearly':
    case 'midi':
    case 'standard_yearly':
      return <StandardIcon className={styles.planIcon} />;
    case 'studio':
    case 'studio_yearly':
      return <StudioIcon className={styles.planIcon} />;
    case 'pro':
    case 'pro_yearly':
    case 'maxi':
    case 'premium_yearly':
      return <PremiumIcon className={styles.planIcon} />;
    case 'deluxe':
    case 'deluxe_yearly':
      return <DeluxeIcon className={styles.planIcon} />;
    default:
      return null;
  }
};

const isPopularPlan = (name: string) =>
  ['premium', 'pro'].some(planName => name.toLowerCase().includes(planName)) &&
  !name.toLowerCase().includes('freemium');

const PlanCardColumn: React.FC<Props> = ({ plan, organization, subscription }) => {
  const isPartnerIxDF = organization.partnerCouponName === 'IXDF';

  const showCouponLabel = () => {
    const freemiumCoupons = ['UPWORKFREE', 'CODEABLEFREE'];
    if (subscription.state !== 'trialing' || !organization.validPartnerCoupon)
      return false;
    if (
      organization.partnerCouponName &&
      freemiumCoupons.includes(organization.partnerCouponName)
    ) {
      return plan.handle === 'freemium';
    }
    return true;
  };

  const planTitle = plan.name.replace('Yearly', '').trim();
  const intervalLabel = plan.interval === 'Yearly' ? '/ yr' : '/ mo';
  const popular = isPopularPlan(plan.name);

  return (
    <div className={styles.card}>
      {popular && <div className={styles.popularBadge}>POPULAR</div>}
      {popular && <PinIcon className={styles.pinIcon} />}

      <div className={styles.planName}>
        {planTitle}
        {getPlanIcon(plan.handle)}
      </div>

      <div className={styles.priceSection}>
        <div className={styles.priceRow}>
          <span className={styles.priceAmount}>${plan.price}</span>
          <span className={styles.pricePeriod}>{intervalLabel}</span>
        </div>
        <div className={styles.ctaWrapper}>
          <SubscribeButton
            plan={plan}
            organization={organization}
            subscription={subscription}
          />
        </div>
        {plan.interval === 'Yearly' &&
          (isPartnerIxDF ? (
            <em className={styles.savings}>{columnStrings.threeMonthsFree}</em>
          ) : (
            <em className={styles.savings}>{columnStrings.twoMonthsFree}</em>
          ))}
        {showCouponLabel() && (
          <em className={styles.savings}>
            {strings.couponToBeApplied}&nbsp;
            <Tooltip
              title={
                organization.activePartnerCouponDescription +
                ' will be applied when you subscribe.'
              }
              getPopupContainer={getPopupContainer}
              placement="bottomRight"
            >
              <span>
                <InfoCircleOutlined style={{ color: '#20c0e7' }} />
              </span>
            </Tooltip>
          </em>
        )}
      </div>

      <div className={styles.membersBadge}>
        <div className={styles.membersBadgeIcon}>
          <User style={{ fontSize: '9px', color: '#19222a' }} />
        </div>
        <span className={styles.membersBadgeText}>
          {!plan.maxMembers
            ? strings.unlimitedUsers
            : pluralize(plan.maxMembers, 'Member')}
        </span>
      </div>

      {getPlanDescription({ planHandle: plan.handle }) && (
        <p className={styles.description}>
          {getPlanDescription({ planHandle: plan.handle })}
        </p>
      )}

      <div className={styles.divider} />

      <div className={styles.features}>
        {(() => {
          const sections: Array<{ header?: string; items: PlanFeatureItem[] }> = [];
          let current: { header?: string; items: PlanFeatureItem[] } = { items: [] };
          for (const item of getPlanFeatures({ planHandle: plan.handle }).filter(item => item.variant)) {
            if (item.type === 'header') {
              sections.push(current);
              current = { header: item.text, items: [] };
            } else {
              current.items.push(item);
            }
          }
          sections.push(current);
          return sections.map((section, i) => (
            <React.Fragment key={`section-${i}`}>
              {section.header && (
                <div className={styles.featureHeader}>{section.header}</div>
              )}
              {section.items.length > 0 && (
                <ul>
                  {section.items.map((item, itemIndex) => (
                    <FeatureColumn
                      key={`${item.type}-${item.text}-${itemIndex}`}
                      item={item}
                    />
                  ))}
                </ul>
              )}
            </React.Fragment>
          ));
        })()}
      </div>
    </div>
  );
};

export default PlanCardColumn;
